import { useEffect, useState } from "react";
import { MonitorPlay, Copy, RotateCcw, Save, KeyRound, Mail, Info, Star, Trash2, AlertTriangle, QrCode } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import QRStickerModal from "@/components/QRStickerModal";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function DemoSetup() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [qrOpen, setQrOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/demo");
    setState(data);
  };
  useEffect(() => { load(); }, []);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); toast.success(okMsg); await load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(""); }
  };

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };

  if (!state) return null;

  return (
    <div className="vb-fade-in max-w-3xl" data-testid="demo-setup-page">
      <div className="flex items-center gap-3 mb-1">
        <MonitorPlay size={26} style={{ color: "var(--vb-accent)" }} />
        <h1 className="vb-heading text-3xl" style={{ color: "var(--vb-text-2)" }}>Demo Setup</h1>
      </div>
      <p className="opacity-70 mb-6">
        Your personal sales demo workspace. Set it up once, save it as your baseline, and restore it to that
        baseline before each client demo. Demo accounts stay out of the paying-tenants list and are never billed.
      </p>

      {!state.exists ? (
        <div className="vb-card p-6" data-testid="demo-provision-card">
          <p className="mb-2">You don't have a demo account yet.</p>
          <p className="text-sm opacity-70 mb-4">
            We'll create a demo workspace and an owner login for you:
            <br /><strong>{state.suggested_email}</strong>
            {state.master_exists && (
              <><br /><span className="text-emerald-700">It will be pre-loaded with the platform default demo setup.</span></>
            )}
          </p>
          <button className="vb-btn" disabled={busy === "provision"}
            onClick={() => run("provision", () => api.post("/admin/demo/provision"), "Demo account created")}
            data-testid="provision-demo">
            {busy === "provision" ? "Creating…" : "Create my demo account"}
          </button>
        </div>
      ) : (
        <div className="grid gap-5">
          <div className="vb-card p-6" data-testid="demo-credentials">
            <h2 className="m-0 mb-1 text-lg" style={{ color: "var(--vb-text-2)" }}>{state.setup.company_name}</h2>
            <p className="text-sm opacity-70 mb-4">Log out and sign in with these credentials to present the demo like a normal user.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase font-semibold opacity-70 flex items-center gap-1"><Mail size={12} /> Login email</label>
                <div className="flex items-center gap-2 mt-1">
                  <input className="vb-input" readOnly value={state.setup.owner_email} data-testid="demo-email" />
                  <button className="vb-btn ghost sm" onClick={() => copy(state.setup.owner_email)} title="Copy"><Copy size={14} /></button>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase font-semibold opacity-70 flex items-center gap-1"><KeyRound size={12} /> Password</label>
                <div className="flex items-center gap-2 mt-1">
                  <input className="vb-input" readOnly value={state.setup.owner_password} data-testid="demo-password" />
                  <button className="vb-btn ghost sm" onClick={() => copy(state.setup.owner_password)} title="Copy"><Copy size={14} /></button>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--vb-border)" }}>
              <label className="text-xs uppercase font-semibold opacity-70">Change demo password</label>
              <div className="flex items-center gap-2 mt-1 max-w-md">
                <input className="vb-input" placeholder="New password (min 4 chars)" value={newPw}
                  onChange={(e) => setNewPw(e.target.value)} data-testid="demo-new-password" />
                <button className="vb-btn" disabled={busy === "pw" || newPw.trim().length < 4}
                  onClick={() => run("pw", async () => { await api.post("/admin/demo/set-password", { password: newPw.trim() }); setNewPw(""); }, "Demo password updated")}
                  data-testid="update-demo-password">
                  {busy === "pw" ? "Updating…" : "Update"}
                </button>
              </div>
            </div>
          </div>

          <div className="vb-card p-6" data-testid="demo-baseline">
            <h2 className="m-0 mb-1 text-lg" style={{ color: "var(--vb-text-2)" }}>Your baseline</h2>
            <div className="text-sm opacity-70 mb-4 flex items-start gap-2">
              <Info size={15} className="mt-0.5 shrink-0" />
              <span>Arrange the demo the way you want (log in as above), then save it as your baseline. "Restore to default" reverts the demo data back to this snapshot — handy between clients.</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm opacity-80 mb-4">
              <div><span className="opacity-60">Baseline saved: </span>{fmt(state.setup.baseline_saved_at)}</div>
              <div><span className="opacity-60">Last restored: </span>{fmt(state.setup.restored_at)}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="vb-btn" disabled={busy === "save"}
                onClick={() => run("save", () => api.post("/admin/demo/save-baseline"), "Baseline saved from current demo state")}
                data-testid="save-baseline">
                <Save size={15} /> {busy === "save" ? "Saving…" : "Save current state as baseline"}
              </button>
              <button className="vb-btn ghost" disabled={busy === "restore" || !state.setup.baseline_saved_at}
                onClick={() => run("restore", () => api.post("/admin/demo/restore"), "Demo restored to baseline")}
                data-testid="restore-baseline">
                <RotateCcw size={15} /> {busy === "restore" ? "Restoring…" : "Restore to default"}
              </button>
            </div>
          </div>

          {state.can_set_master && (
            <div className="vb-card p-6" data-testid="demo-master-template">
              <h2 className="m-0 mb-1 text-lg flex items-center gap-2" style={{ color: "var(--vb-text-2)" }}>
                <Star size={17} style={{ color: "var(--vb-accent)" }} /> Platform default template
              </h2>
              <div className="text-sm opacity-70 mb-4 flex items-start gap-2">
                <Info size={15} className="mt-0.5 shrink-0" />
                <span>
                  Snapshot <strong>your current demo</strong> and make it the starting point for <strong>every new
                  sales/admin demo</strong>. New staff get a demo pre-loaded with this setup, then tailor and save their own baseline.
                </span>
              </div>
              <div className="text-sm opacity-80 mb-4">
                <span className="opacity-60">Current platform default: </span>
                {state.master_exists ? `set ${fmt(state.master_saved_at)}` : "not set yet"}
              </div>
              <button className="vb-btn" disabled={busy === "master"}
                onClick={() => run("master", () => api.post("/admin/demo/set-master"),
                  "Your demo is now the platform default for new demos")}
                data-testid="set-master-template">
                <Star size={15} /> {busy === "master" ? "Saving…" : "Set my demo as platform default"}
              </button>
            </div>
          )}

          <div className="vb-card p-6" data-testid="demo-qr-stickers">
            <h2 className="m-0 mb-1 text-lg flex items-center gap-2"
              style={{ color: "var(--vb-text-2)" }}>
              <QrCode size={17} /> QR sticker printing
            </h2>
            <p className="text-sm opacity-75 mb-3">
              Print thermal QR stickers for every site, facility, and equipment
              piece in your demo workspace — same flow the sales team uses for
              real tenants. Handy for on-site walk-throughs where prospects can
              scan the stickers straight from their phone.
            </p>
            <button
              className="vb-btn"
              onClick={() => setQrOpen(true)}
              data-testid="demo-open-qr-stickers"
            >
              <QrCode size={15} /> Open QR sticker printer
            </button>
          </div>

          <div className="vb-card p-6" data-testid="demo-danger-zone"
            style={{ borderColor: "#c45454", borderWidth: 1, borderStyle: "solid" }}>
            <h2 className="m-0 mb-1 text-lg flex items-center gap-2" style={{ color: "#b91c1c" }}>
              <AlertTriangle size={17} /> Delete all data
            </h2>
            <div className="text-sm opacity-80 mb-3 flex items-start gap-2">
              <Info size={15} className="mt-0.5 shrink-0" />
              <span>
                Permanently clears <strong>all logged records</strong> from this demo — daily checks, CCP readings &amp;
                sign-offs, incident reports, filed audit-vault reports, disposal &amp; pest logs, training records and
                notifications. Your <strong>setup is kept</strong> (sites, facilities, equipment, checklists, team) so
                you can start capturing fresh data right away. Affects <strong>this demo account only</strong> — never
                any real customer. This cannot be undone.
              </span>
            </div>
            {state.setup.records_cleared_at && (
              <div className="text-xs opacity-70 mb-3">Last cleared: {fmt(state.setup.records_cleared_at)}</div>
            )}
            <label className="text-xs uppercase font-semibold opacity-70">Type <strong>DELETE</strong> to confirm</label>
            <div className="flex items-center gap-2 mt-1 max-w-md flex-wrap">
              <input className="vb-input" placeholder="DELETE" value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)} data-testid="demo-reset-confirm" />
              <button className="vb-btn"
                style={{ background: "#b91c1c", borderColor: "#b91c1c", color: "#fff" }}
                disabled={busy === "reset" || confirmText.trim().toUpperCase() !== "DELETE"}
                onClick={() => run("reset", async () => {
                  const { data } = await api.post("/admin/demo/reset-records", { confirm: confirmText.trim() });
                  setConfirmText("");
                  return data;
                }, "Demo records cleared — you're starting fresh")}
                data-testid="demo-reset-records">
                <Trash2 size={15} /> {busy === "reset" ? "Clearing…" : "Delete all data"}
              </button>
            </div>
          </div>
        </div>
      )}

      {qrOpen && state.exists && state.tenant && (
        <QRStickerModal
          tenant={state.tenant}
          onClose={() => setQrOpen(false)}
        />
      )}
    </div>
  );
}
